import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { uploadToFirebaseStorage, deleteFromFirebaseStorage, PRESET_LOGO, PRESET_PRODUCT } from '../../utils/firebaseStorage';
import '../AdminSettings/AdminSettings.css';

// ── SVG Icons ──────────────────────────────────────────────────
const PlusIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);
const TrashIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6M14 11v6" />
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
);
const RemoveIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);
const UploadIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
);
const SpinnerIcon = () => (
    <svg className="as-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
);
const FileIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="12" y1="18" x2="12" y2="12" />
        <line x1="9" y1="15" x2="15" y2="15" />
    </svg>
);
const YouTubeIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
);

// ── Toggle Switch (reusing from StoreSettings pattern) ─────────
function Toggle({ checked, onChange }) {
    return (
        <label className="ss-toggle">
            <input type="checkbox" checked={checked} onChange={onChange} />
            <span className="ss-toggle-track">
                <span className="ss-toggle-thumb" />
            </span>
        </label>
    );
}

// ── YouTube URL parser ─────────────────────────────────────────
function extractYouTubeId(url) {
    if (!url) return null;
    const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
        /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    ];
    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) return match[1];
    }
    return null;
}

// ── Collection Image Item Component ───────────────────────────
function CollectionImageItem({ collection, imageUrl, onImageChange, onImageRemove }) {
    const [open, setOpen] = useState(false);

    return (
        <div className="ss-method-card">
            <div className="ss-method-card-header" onClick={() => setOpen(o => !o)}>
                <div className="ss-method-card-left">
                    <span className={`ss-method-card-chevron ${open ? 'ss-method-card-chevron--open' : ''}`}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </span>
                    {imageUrl ? (
                        <div className="hs-banner-thumb">
                            <img src={imageUrl} alt={collection.title} />
                        </div>
                    ) : (
                        <div className="hs-banner-thumb hs-banner-thumb--empty">
                            <FileIcon />
                        </div>
                    )}
                    <span className="ss-method-card-name">
                        {collection.title}
                        {!imageUrl && <span className="ss-method-card-placeholder"> — Using default image</span>}
                    </span>
                </div>
            </div>

            {open && (
                <div className="ss-method-card-body">
                    <div className="hs-banner-upload-row">
                        <div className="settings-upload-box-wrap">
                            <label className="settings-section-label">Collection Image</label>
                            <div className="settings-upload-box hs-banner-upload-box">
                                {imageUrl ? (
                                    <img src={imageUrl} alt={collection.title} />
                                ) : (
                                    <>
                                        <FileIcon />
                                        <span>Choose a file or drag and drop it here</span>
                                    </>
                                )}
                                <input 
                                    type="file" 
                                    accept="image/*" 
                                    onChange={(e) => onImageChange(collection.id, e.target.files[0])}
                                />
                            </div>
                            <div className="settings-upload-btns">
                                <button className="settings-btn-remove" onClick={() => onImageRemove(collection.id)}>
                                    <RemoveIcon /> Remove
                                </button>
                                <label className="settings-btn-upload">
                                    <UploadIcon /> Upload file
                                    <input 
                                        type="file" 
                                        accept="image/*" 
                                        onChange={(e) => onImageChange(collection.id, e.target.files[0])}
                                    />
                                </label>
                            </div>
                        </div>

                        <div className="hs-banner-info">
                            <p className="as-section-desc" style={{ marginTop: 0 }}>
                                Recommended size: <strong>500 × 500px</strong>. Supported formats: JPG, PNG, WebP.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Card Component (Collab/FeaturedClients Item) ────────────────
function CardItem({ item, index, type, onUpdate, onDelete }) {
    const [open, setOpen] = useState(false);
    const isCollab = type === 'collab';

    function handleImageChange(e) {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => onUpdate({ ...item, imageUrl: ev.target.result, imageFile: file });
        reader.readAsDataURL(file);
    }

    function handleImageRemove() {
        onUpdate({ ...item, imageUrl: '', imageFile: null });
    }

    function handleFieldChange(field, value) {
        onUpdate({ ...item, [field]: value });
    }

    return (
        <div className={`ss-method-card ${item.enabled ? '' : 'ss-method-card--disabled'}`}>
            <div className="ss-method-card-header" onClick={() => setOpen(o => !o)}>
                <div className="ss-method-card-left">
                    <span className={`ss-method-card-chevron ${open ? 'ss-method-card-chevron--open' : ''}`}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </span>
                    {/* Thumbnail preview in header */}
                    {item.imageUrl ? (
                        <div className="hs-banner-thumb">
                            <img src={item.imageUrl} alt={item.title} />
                        </div>
                    ) : (
                        <div className="hs-banner-thumb hs-banner-thumb--empty">
                            <FileIcon />
                        </div>
                    )}
                    <span className="ss-method-card-name">
                        {item.title || `${isCollab ? 'Collab' : 'Client'} ${index + 1}`}
                        {!item.imageUrl && <span className="ss-method-card-placeholder"> — No image uploaded</span>}
                    </span>
                    <span className={`ss-method-badge ${item.enabled ? 'ss-method-badge--on' : 'ss-method-badge--off'}`}>
                        {item.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                </div>
                <div className="ss-method-card-right" onClick={e => e.stopPropagation()}>
                    <Toggle checked={item.enabled} onChange={() => onUpdate({ ...item, enabled: !item.enabled })} />
                    <button className="ss-icon-btn ss-icon-btn--danger" onClick={() => onDelete(item.id)} title="Delete">
                        <TrashIcon />
                    </button>
                </div>
            </div>

            {open && (
                <div className="ss-method-card-body">
                    <div className="hs-banner-upload-row">
                        {/* Image upload box */}
                        <div className="settings-upload-box-wrap">
                            <label className="settings-section-label">{isCollab ? 'Collab' : 'Client'} Image</label>
                            <div className="settings-upload-box hs-banner-upload-box">
                                {item.imageUrl ? (
                                    <img src={item.imageUrl} alt={item.title} />
                                ) : (
                                    <>
                                        <FileIcon />
                                        <span>Choose a file or drag and drop it here</span>
                                    </>
                                )}
                                <input type="file" accept="image/*" onChange={handleImageChange} />
                            </div>
                            <div className="settings-upload-btns">
                                <button className="settings-btn-remove" onClick={handleImageRemove}>
                                    <RemoveIcon /> Remove
                                </button>
                                <label className="settings-btn-upload">
                                    <UploadIcon /> Upload file
                                    <input type="file" accept="image/*" onChange={handleImageChange} />
                                </label>
                            </div>
                        </div>

                        {/* Info & Fields */}
                        <div className="hs-banner-info">
                            <div className="settings-field">
                                <label>Title</label>
                                <input
                                    type="text"
                                    placeholder={isCollab ? "e.g., Small Businesses" : "e.g., School / Barangay"}
                                    value={item.title || ''}
                                    onChange={e => handleFieldChange('title', e.target.value)}
                                />
                            </div>
                            <p className="as-section-desc" style={{ marginTop: 16, marginBottom: 0 }}>
                                Recommended size: <strong>400 × 400px</strong>. Supported formats: JPG, PNG, WebP.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Banner Accordion Card ──────────────────────────────────────
function BannerCard({ banner, index, onUpdate, onDelete }) {
    const [open, setOpen] = useState(false);

    function handleImageChange(e) {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => onUpdate({ ...banner, imageUrl: ev.target.result, imageFile: file });
        reader.readAsDataURL(file);
    }

    function handleImageRemove() {
        onUpdate({ ...banner, imageUrl: '', imageFile: null });
    }

    return (
        <div className={`ss-method-card ${banner.enabled ? '' : 'ss-method-card--disabled'}`}>
            <div className="ss-method-card-header" onClick={() => setOpen(o => !o)}>
                <div className="ss-method-card-left">
                    <span className={`ss-method-card-chevron ${open ? 'ss-method-card-chevron--open' : ''}`}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </span>
                    {/* Thumbnail preview in header */}
                    {banner.imageUrl ? (
                        <div className="hs-banner-thumb">
                            <img src={banner.imageUrl} alt={`Banner ${index + 1}`} />
                        </div>
                    ) : (
                        <div className="hs-banner-thumb hs-banner-thumb--empty">
                            <FileIcon />
                        </div>
                    )}
                    <span className="ss-method-card-name">
                        Banner {index + 1}
                        {!banner.imageUrl && <span className="ss-method-card-placeholder"> — No image uploaded</span>}
                    </span>
                    <span className={`ss-method-badge ${banner.enabled ? 'ss-method-badge--on' : 'ss-method-badge--off'}`}>
                        {banner.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                </div>
                <div className="ss-method-card-right" onClick={e => e.stopPropagation()}>
                    <Toggle checked={banner.enabled} onChange={() => onUpdate({ ...banner, enabled: !banner.enabled })} />
                    <button className="ss-icon-btn ss-icon-btn--danger" onClick={() => onDelete(banner.id)} title="Delete banner">
                        <TrashIcon />
                    </button>
                </div>
            </div>

            {open && (
                <div className="ss-method-card-body">
                    <div className="hs-banner-upload-row">
                        {/* Image upload box */}
                        <div className="settings-upload-box-wrap">
                            <label className="settings-section-label">Banner Image</label>
                            <div className="settings-upload-box hs-banner-upload-box">
                                {banner.imageUrl ? (
                                    <img src={banner.imageUrl} alt={`Banner ${index + 1}`} />
                                ) : (
                                    <>
                                        <FileIcon />
                                        <span>Choose a file or drag and drop it here</span>
                                    </>
                                )}
                                <input type="file" accept="image/*" onChange={handleImageChange} />
                            </div>
                            <div className="settings-upload-btns">
                                <button className="settings-btn-remove" onClick={handleImageRemove}>
                                    <RemoveIcon /> Remove
                                </button>
                                <label className="settings-btn-upload">
                                    <UploadIcon /> Upload file
                                    <input type="file" accept="image/*" onChange={handleImageChange} />
                                </label>
                            </div>
                        </div>

                        {/* Banner info note */}
                        <div className="hs-banner-info">
                            <p className="as-section-desc" style={{ marginTop: 0 }}>
                                Upload the image you want to display in the homepage banner carousel.
                                Recommended size: <strong>1400 × 450px</strong>. Supported formats: JPG, PNG, WebP.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Main Component ─────────────────────────────────────────────
export default function HomepageSettings() {

    // ── Video Banner state ─────────────────────────────────────
    const [videoBanner, setVideoBanner] = useState({
        youtubeUrl: '',
        tagline: '',
        enabled: true,
    });
    const [videoDraft, setVideoDraft] = useState({ ...videoBanner });
    const [videoEditing, setVideoEditing] = useState(false);
    const [videoMsg, setVideoMsg] = useState(null);
    const [youtubeId, setYoutubeId] = useState(null);

    // ── Promotional Banners state ──────────────────────────────
    const [banners, setBanners] = useState([]);
    const [bannersDirty, setBannersDirty] = useState(false);
    const [bannersMsg, setBannersMsg] = useState(null);

    // ── Collab state ───────────────────────────────────────────
    const [collabs, setCollabs] = useState([]);
    const [collabsDirty, setCollabsDirty] = useState(false);
    const [collabsMsg, setCollabsMsg] = useState(null);

    // ── Featured Clients state ─────────────────────────────────
    const [featuredClients, setFeaturedClients] = useState([]);
    const [clientsDirty, setClientsDirty] = useState(false);
    const [clientsMsg, setClientsMsg] = useState(null);

    // ── Collections Images state ───────────────────────────────
    const [collectionImages, setCollectionImages] = useState({});
    const [collectionImagesDirty, setCollectionImagesDirty] = useState(false);
    const [collectionImagesMsg, setCollectionImagesMsg] = useState(null);

    // ── Saving states ──────────────────────────────────────────
    const [bannersSaving, setBannersSaving] = useState(false);
    const [collabsSaving, setCollabsSaving] = useState(false);
    const [clientsSaving, setClientsSaving] = useState(false);
    const [collectionImagesSaving, setCollectionImagesSaving] = useState(false);

    const FIXED_COLLECTIONS = [
        { id: 'clothing', title: 'Clothing', filter: 'Clothing' },
        { id: 'accessories', title: 'Accessories', filter: 'Accessories' },
        { id: 'printed', title: 'Printed Materials', filter: 'Printed Materials' },
        { id: 'promotional', title: 'Promotional Items', filter: 'Promotional Materials' },
        { id: 'packaging', title: 'Packaging & Display', filter: 'Packaging & Display' },
        { id: 'sublimation', title: 'Full Sublimation', filter: 'Full Sublimation' },
    ];

    useEffect(() => { loadSettings(); }, []);

    async function loadSettings() {
        try {
            const snap = await getDoc(doc(db, 'settings', 'homepage'));
            if (snap.exists()) {
                const data = snap.data();

                const video = {
                    youtubeUrl: data.youtubeUrl || '',
                    tagline: data.tagline || '',
                    enabled: data.videoBannerEnabled !== false,
                };
                setVideoBanner(video);
                setVideoDraft(video);
                setYoutubeId(extractYouTubeId(data.youtubeUrl || ''));

                setBanners(data.promotionalBanners || []);

                setCollabs(data.collabs || []);

                setFeaturedClients(data.featuredClients || []);

                const raw = data.collectionImages || {};
                const normalized = {};
                for (const id in raw) {
                    const val = raw[id];
                    normalized[id] = typeof val === 'string' ? { imageUrl: val, imageFile: null } : val;
                }
                setCollectionImages(normalized);
            }
        } catch (err) {
            console.error('Error loading homepage settings:', err);
        }
    }

    // ── Video Banner handlers ──────────────────────────────────
    function handleVideoEdit() {
        setVideoDraft({ ...videoBanner });
        setVideoEditing(true);
        setVideoMsg(null);
    }
    function handleVideoCancel() {
        setVideoDraft({ ...videoBanner });
        setYoutubeId(extractYouTubeId(videoBanner.youtubeUrl));
        setVideoEditing(false);
        setVideoMsg(null);
    }
    async function handleVideoSave() {
        const id = extractYouTubeId(videoDraft.youtubeUrl);
        if (videoDraft.youtubeUrl && !id) {
            setVideoMsg({ type: 'error', text: 'Please enter a valid YouTube URL.' });
            return;
        }
        try {
            await setDoc(doc(db, 'settings', 'homepage'), {
                youtubeUrl: videoDraft.youtubeUrl,
                tagline: videoDraft.tagline,
                videoBannerEnabled: videoDraft.enabled,
            }, { merge: true });

            setVideoBanner({ ...videoDraft });
            setYoutubeId(id);
            setVideoEditing(false);
            setVideoMsg({ type: 'success', text: 'Video banner settings saved successfully.' });
        } catch (err) {
            setVideoMsg({ type: 'error', text: 'Failed to save. Please try again.' });
        }
    }
    
    function handleUrlChange(url) {
        setVideoDraft(d => ({ ...d, youtubeUrl: url }));
        setYoutubeId(extractYouTubeId(url));
    }

    // ── Promotional Banners handlers ───────────────────────────
    function handleAddBanner() {
        const newBanner = {
            id: Date.now().toString(),
            imageUrl: '',
            imageFile: null,
            enabled: true,
        };
        setBanners(prev => [...prev, newBanner]);
        setBannersDirty(true);
    }
    function handleUpdateBanner(updated) {
        setBanners(prev => prev.map(b => b.id === updated.id ? updated : b));
        setBannersDirty(true);
    }
    async function handleDeleteBanner(id) {
        const banner = banners.find(b => b.id === id);
        if (banner?.imageUrl) {
            const { success, error } = await deleteFromFirebaseStorage(banner.imageUrl);
            if (!success) console.warn('Failed to delete banner image:', error);
        }
        try {
            await setDoc(doc(db, 'settings', 'homepage'), {
                promotionalBanners: banners.filter(b => b.id !== id),
            }, { merge: true });
            setBanners(prev => prev.filter(b => b.id !== id));
            setBannersMsg({ type: 'success', text: 'Banner deleted successfully.' });
        } catch (err) {
            setBannersMsg({ type: 'error', text: 'Failed to delete banner. Please try again.' });
        }
    }
    async function handleSaveBanners() {
        setBannersSaving(true);
        try {
            const toSave = await Promise.all(
                banners.map(async (banner) => {
                    let imageUrl = banner.imageUrl || '';
                    if (banner.imageFile) {
                        const { url, error: uploadErr } = await uploadToFirebaseStorage(banner.imageFile, 'jg-printing/banners', PRESET_PRODUCT);
                        if (uploadErr) throw new Error('Banner upload failed: ' + uploadErr);
                        imageUrl = url || '';
                    }
                    return { id: banner.id, imageUrl, enabled: banner.enabled !== false };
                })
            );
            await setDoc(doc(db, 'settings', 'homepage'), {
                promotionalBanners: toSave,
            }, { merge: true });

            setBanners(toSave);
            setBannersDirty(false);
            setBannersMsg({ type: 'success', text: 'Promotional banners saved successfully.' });
        } catch (err) {
            setBannersMsg({ type: 'error', text: err.message || 'Failed to save. Please try again.' });
        } finally {
            setBannersSaving(false);
        }
    }
    function handleCancelBanners() {
        loadSettings();
        setBannersDirty(false);
        setBannersMsg(null);
    }

    // ── Collab handlers ────────────────────────────────────────
    function handleAddCollab() {
        const newCollab = {
            id: Date.now().toString(),
            title: '',
            imageUrl: '',
            imageFile: null,
            enabled: true,
        };
        setCollabs(prev => [...prev, newCollab]);
        setCollabsDirty(true);
    }
    function handleUpdateCollab(updated) {
        setCollabs(prev => prev.map(c => c.id === updated.id ? updated : c));
        setCollabsDirty(true);
    }
    async function handleDeleteCollab(id) {
        const collab = collabs.find(c => c.id === id);
        if (collab?.imageUrl) {
            const { success, error } = await deleteFromFirebaseStorage(collab.imageUrl);
            if (!success) console.warn('Failed to delete collab image:', error);
        }
        try {
            await setDoc(doc(db, 'settings', 'homepage'), {
                collabs: collabs.filter(c => c.id !== id),
            }, { merge: true });
            setCollabs(prev => prev.filter(c => c.id !== id));
            setCollabsMsg({ type: 'success', text: 'Collaboration deleted successfully.' });
        } catch (err) {
            setCollabsMsg({ type: 'error', text: 'Failed to delete collaboration. Please try again.' });
        }
    }
    async function handleSaveCollabs() {
        setCollabsSaving(true);
        try {
            const toSave = await Promise.all(
                collabs.map(async (collab) => {
                    let imageUrl = collab.imageUrl || '';
                    if (collab.imageFile) {
                        const { url, error: uploadErr } = await uploadToFirebaseStorage(collab.imageFile, 'jg-printing/collabs', PRESET_PRODUCT);
                        if (uploadErr) throw new Error('Image upload failed: ' + uploadErr);
                        imageUrl = url || '';
                    }
                    return { id: collab.id, title: collab.title, imageUrl, enabled: collab.enabled !== false };
                })
            );
            await setDoc(doc(db, 'settings', 'homepage'), {
                collabs: toSave,
            }, { merge: true });

            setCollabs(toSave);
            setCollabsDirty(false);
            setCollabsMsg({ type: 'success', text: 'Collaborations saved successfully.' });
        } catch (err) {
            setCollabsMsg({ type: 'error', text: err.message || 'Failed to save. Please try again.' });
        } finally {
            setCollabsSaving(false);
        }
    }
    function handleCancelCollabs() {
        loadSettings();
        setCollabsDirty(false);
        setCollabsMsg(null);
    }

    // ── Featured Clients handlers ──────────────────────────────
    function handleAddClient() {
        const newClient = {
            id: Date.now().toString(),
            title: '',
            imageUrl: '',
            imageFile: null,
            enabled: true,
        };
        setFeaturedClients(prev => [...prev, newClient]);
        setClientsDirty(true);
    }
    function handleUpdateClient(updated) {
        setFeaturedClients(prev => prev.map(c => c.id === updated.id ? updated : c));
        setClientsDirty(true);
    }
    async function handleDeleteClient(id) {
        const client = featuredClients.find(c => c.id === id);
        if (client?.imageUrl) {
            const { success, error } = await deleteFromFirebaseStorage(client.imageUrl);
            if (!success) console.warn('Failed to delete client image:', error);
        }
        try {
            await setDoc(doc(db, 'settings', 'homepage'), {
                featuredClients: featuredClients.filter(c => c.id !== id),
            }, { merge: true });
            setFeaturedClients(prev => prev.filter(c => c.id !== id));
            setClientsMsg({ type: 'success', text: 'Featured client deleted successfully.' });
        } catch (err) {
            setClientsMsg({ type: 'error', text: 'Failed to delete featured client. Please try again.' });
        }
    }
    async function handleSaveClients() {
        setClientsSaving(true);
        try {
            const toSave = await Promise.all(
                featuredClients.map(async (client) => {
                    let imageUrl = client.imageUrl || '';
                    if (client.imageFile) {
                        const { url, error: uploadErr } = await uploadToFirebaseStorage(client.imageFile, 'jg-printing/clients', PRESET_LOGO);
                        if (uploadErr) throw new Error('Image upload failed: ' + uploadErr);
                        imageUrl = url || '';
                    }
                    return { id: client.id, title: client.title, imageUrl, enabled: client.enabled !== false };
                })
            );
            await setDoc(doc(db, 'settings', 'homepage'), {
                featuredClients: toSave,
            }, { merge: true });

            setFeaturedClients(toSave);
            setClientsDirty(false);
            setClientsMsg({ type: 'success', text: 'Featured clients saved successfully.' });
        } catch (err) {
            setClientsMsg({ type: 'error', text: err.message || 'Failed to save. Please try again.' });
        } finally {
            setClientsSaving(false);
        }
    }
    function handleCancelClients() {
        loadSettings();
        setClientsDirty(false);
        setClientsMsg(null);
    }

    // ── Collection Images handlers ─────────────────────────────
    function handleCollectionImageChange(collectionId, file) {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            setCollectionImages(prev => ({
                ...prev,
                [collectionId]: { imageUrl: ev.target.result, imageFile: file }
            }));
            setCollectionImagesDirty(true);
        };
        reader.readAsDataURL(file);
    }

    async function handleCollectionImageRemove(collectionId) {
        const existing = collectionImages[collectionId];
        const url = existing?.imageUrl || (typeof existing === 'string' ? existing : null);
        if (url) {
            const { success, error } = await deleteFromFirebaseStorage(url);
            if (!success) console.warn('Failed to delete collection image:', error);
        }
        try {
            const next = { ...collectionImages };
            delete next[collectionId];
            await setDoc(doc(db, 'settings', 'homepage'), {
                collectionImages: next,
            }, { merge: true });
            setCollectionImages(next);
            setCollectionImagesMsg({ type: 'success', text: 'Collection image deleted.' });
        } catch (err) {
            setCollectionImagesMsg({ type: 'error', text: 'Failed to delete. Please try again.' });
        }
    }

    async function handleSaveCollectionImages() {
        setCollectionImagesSaving(true);
        try {
            const entries = Object.entries(collectionImages);
            const toSaveEntries = await Promise.all(
                entries.map(async ([collectionId, item]) => {
                    let imageUrl;
                    if (typeof item === 'string') {
                        imageUrl = item;
                    } else {
                        imageUrl = item.imageUrl || '';
                        if (item.imageFile) {
                            const { url, error: uploadErr } = await uploadToFirebaseStorage(item.imageFile, 'jg-printing/collections', PRESET_PRODUCT);
                            if (uploadErr) throw new Error('Image upload failed: ' + uploadErr);
                            imageUrl = url || '';
                        }
                    }
                    return [collectionId, imageUrl];
                })
            );
            const toSave = Object.fromEntries(toSaveEntries);
            await setDoc(doc(db, 'settings', 'homepage'), {
                collectionImages: toSave,
            }, { merge: true });

            const normalized = {};
            for (const id in toSave) {
                normalized[id] = { imageUrl: toSave[id], imageFile: null };
            }
            setCollectionImages(normalized);
            setCollectionImagesDirty(false);
            setCollectionImagesMsg({ type: 'success', text: 'Collection images saved successfully.' });
        } catch (err) {
            setCollectionImagesMsg({ type: 'error', text: err.message || 'Failed to save. Please try again.' });
        } finally {
            setCollectionImagesSaving(false);
        }
    }

    function handleCancelCollectionImages() {
        loadSettings();
        setCollectionImagesDirty(false);
        setCollectionImagesMsg(null);
    }

    // ── Render ─────────────────────────────────────────────────
    return (
        <div>

            {/* ── Video Banner Card ── */}
            <div className="settings-card">
                <div className="settings-card-header">
                    <h2 className="settings-card-title">Video Banner</h2>
                    <div className="settings-card-actions">
                        {!videoEditing ? (
                            <button className="settings-btn-edit" onClick={handleVideoEdit}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                                Edit
                            </button>
                        ) : (
                            <>
                                <button className="settings-btn-cancel" onClick={handleVideoCancel}>Cancel</button>
                                <button className="settings-btn-save" onClick={handleVideoSave}>Save Changes</button>
                            </>
                        )}
                    </div>
                </div>

                {videoMsg && (
                    <div className={`settings-msg settings-msg--${videoMsg.type}`}>{videoMsg.text}</div>
                )}

                <div className="hs-video-layout">
                    {/* Left — fields */}
                    <div className="hs-video-fields">
                        <div className="settings-field">
                            <label>YouTube URL</label>
                            <div className="hs-url-input-wrap">
                                <span className="hs-url-icon"><YouTubeIcon /></span>
                                <input
                                    type="text"
                                    placeholder="https://www.youtube.com/watch?v=..."
                                    value={videoEditing ? videoDraft.youtubeUrl : videoBanner.youtubeUrl}
                                    disabled={!videoEditing}
                                    onChange={e => handleUrlChange(e.target.value)}
                                />
                            </div>
                            {videoEditing && videoDraft.youtubeUrl && !extractYouTubeId(videoDraft.youtubeUrl) && (
                                <span className="hs-url-hint hs-url-hint--error">Invalid YouTube URL</span>
                            )}
                            {videoEditing && extractYouTubeId(videoDraft.youtubeUrl) && (
                                <span className="hs-url-hint hs-url-hint--ok">✓ Valid YouTube URL detected</span>
                            )}
                        </div>

                        <div className="settings-field">
                            <label>Banner Tagline</label>
                            <input
                                type="text"
                                placeholder="Your Tagline Here"
                                value={videoEditing ? videoDraft.tagline : videoBanner.tagline}
                                disabled={!videoEditing}
                                onChange={e => setVideoDraft(d => ({ ...d, tagline: e.target.value }))}
                            />
                            <span className="hs-field-hint">This text appears overlaid on the video banner.</span>
                        </div>

                        <div className="hs-toggle-row">
                            <div>
                                <span className="hs-toggle-label">Show Video Banner</span>
                                <span className="hs-toggle-desc">Display or hide the video banner on the homepage</span>
                            </div>
                            <Toggle
                                checked={videoEditing ? videoDraft.enabled : videoBanner.enabled}
                                onChange={() => {
                                    if (!videoEditing) return;
                                    setVideoDraft(d => ({ ...d, enabled: !d.enabled }));
                                }}
                            />
                        </div>
                    </div>

                    {/* Right — YouTube preview */}
                    <div className="hs-video-preview">
                        <label className="settings-section-label">Preview</label>
                        {youtubeId ? (
                            <div className="hs-iframe-wrap">
                                <iframe
                                    key={youtubeId}
                                    src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&start=0&end=15&controls=1&modestbranding=1&rel=0`}
                                    title="YouTube video preview"
                                    allow="autoplay; encrypted-media"
                                    allowFullScreen
                                    frameBorder="0"
                                />
                            </div>
                        ) : (
                            <div className="hs-preview-empty">
                                <span className="hs-preview-empty-icon"><YouTubeIcon /></span>
                                <span>Paste a YouTube URL to see a preview</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Promotional Banners Card ── */}
            <div className="settings-card">
                <div className="settings-card-header">
                    <h2 className="settings-card-title">Promotional Banners</h2>
                    <div className="settings-card-actions">
                        {bannersDirty && (
                            <>
                                <button className="settings-btn-cancel" onClick={handleCancelBanners} disabled={bannersSaving}>Cancel</button>
                                <button className="settings-btn-save" onClick={handleSaveBanners} disabled={bannersSaving}>
                                    {bannersSaving ? <><SpinnerIcon /> Saving...</> : 'Save Changes'}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {bannersMsg && (
                    <div className={`settings-msg settings-msg--${bannersMsg.type}`}>{bannersMsg.text}</div>
                )}

                <p className="as-section-desc" style={{ marginTop: 0 }}>
                    Upload banner images for the homepage carousel. Only enabled banners will be displayed.
                    Banners appear in the order listed below.
                </p>

                <div className="ss-methods-list">
                    {banners.length === 0 && (
                        <div className="ss-methods-empty">No banners added yet.</div>
                    )}
                    {banners.map((banner, index) => (
                        <BannerCard
                            key={banner.id}
                            banner={banner}
                            index={index}
                            onUpdate={handleUpdateBanner}
                            onDelete={handleDeleteBanner}
                        />
                    ))}
                </div>

                <button className="ss-btn-add" onClick={handleAddBanner}>
                    <PlusIcon /> Add Banner
                </button>
            </div>

            {/* ── Collections Images Card ── */}
            <div className="settings-card">
                <div className="settings-card-header">
                    <h2 className="settings-card-title">Collection Images</h2>
                    <div className="settings-card-actions">
                        {collectionImagesDirty && (
                            <>
                                <button className="settings-btn-cancel" onClick={handleCancelCollectionImages} disabled={collectionImagesSaving}>Cancel</button>
                                <button className="settings-btn-save" onClick={handleSaveCollectionImages} disabled={collectionImagesSaving}>
                                    {collectionImagesSaving ? <><SpinnerIcon /> Saving...</> : 'Save Changes'}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {collectionImagesMsg && (
                    <div className={`settings-msg settings-msg--${collectionImagesMsg.type}`}>{collectionImagesMsg.text}</div>
                )}

                <p className="as-section-desc" style={{ marginTop: 0 }}>
                    Customize the images for your product collections. Leave blank to use the default image.
                </p>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '24px',
                    maxWidth: '1200px'
                }}>
                    {FIXED_COLLECTIONS.map((collection) => {
                        const imageData = collectionImages[collection.id];
                        const imageUrl = imageData?.imageUrl || (imageData && typeof imageData === 'string' ? imageData : null);
                        return (
                            <CollectionImageItem
                                key={collection.id}
                                collection={collection}
                                imageUrl={imageUrl}
                                onImageChange={handleCollectionImageChange}
                                onImageRemove={handleCollectionImageRemove}
                            />
                        );
                    })}
                </div>
            </div>

            {/* ── Collaborations (Collab) Card ── */}
            <div className="settings-card">
                <div className="settings-card-header">
                    <h2 className="settings-card-title">Collaborations</h2>
                    <div className="settings-card-actions">
                        {collabsDirty && (
                            <>
                                <button className="settings-btn-cancel" onClick={handleCancelCollabs} disabled={collabsSaving}>Cancel</button>
                                <button className="settings-btn-save" onClick={handleSaveCollabs} disabled={collabsSaving}>
                                    {collabsSaving ? <><SpinnerIcon /> Saving...</> : 'Save Changes'}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {collabsMsg && (
                    <div className={`settings-msg settings-msg--${collabsMsg.type}`}>{collabsMsg.text}</div>
                )}

                <p className="as-section-desc" style={{ marginTop: 0 }}>
                    Manage your business collaborations. Only enabled collaborations will be displayed on the homepage.
                    You can add photos, titles, and tags for each collaboration.
                </p>

                <div className="ss-methods-list">
                    {collabs.length === 0 && (
                        <div className="ss-methods-empty">No collaborations added yet.</div>
                    )}
                    {collabs.map((item, index) => (
                        <CardItem
                            key={item.id}
                            item={item}
                            index={index}
                            type="collab"
                            onUpdate={handleUpdateCollab}
                            onDelete={handleDeleteCollab}
                        />
                    ))}
                </div>

                <button className="ss-btn-add" onClick={handleAddCollab}>
                    <PlusIcon /> Add Collaboration
                </button>
            </div>

            {/* ── Featured Clients Card ── */}
            <div className="settings-card">
                <div className="settings-card-header">
                    <h2 className="settings-card-title">Featured Clients</h2>
                    <div className="settings-card-actions">
                        {clientsDirty && (
                            <>
                                <button className="settings-btn-cancel" onClick={handleCancelClients} disabled={clientsSaving}>Cancel</button>
                                <button className="settings-btn-save" onClick={handleSaveClients} disabled={clientsSaving}>
                                    {clientsSaving ? <><SpinnerIcon /> Saving...</> : 'Save Changes'}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {clientsMsg && (
                    <div className={`settings-msg settings-msg--${clientsMsg.type}`}>{clientsMsg.text}</div>
                )}

                <p className="as-section-desc" style={{ marginTop: 0 }}>
                    Manage your featured clients. Only enabled clients will be displayed on the homepage.
                    You can add photos and titles for each client.
                </p>

                <div className="ss-methods-list">
                    {featuredClients.length === 0 && (
                        <div className="ss-methods-empty">No featured clients added yet.</div>
                    )}
                    {featuredClients.map((item, index) => (
                        <CardItem
                            key={item.id}
                            item={item}
                            index={index}
                            type="client"
                            onUpdate={handleUpdateClient}
                            onDelete={handleDeleteClient}
                        />
                    ))}
                </div>

                <button className="ss-btn-add" onClick={handleAddClient}>
                    <PlusIcon /> Add Client
                </button>
            </div>

        </div>
    );
}
